import { observer } from 'mobx-react';
import { logUsage } from 'owa-analytics';
import { logMountingPerformance } from 'owa-analytics';
import {
    ToggleFavoriteButton,
    lazyRemoveFavoritePrivateDistributionList,
    lazyAddFavoritePrivateDistributionList,
    isPrivateDLInFavorites,
    getFavoriteIdForPDL,
    isOutlookFavoritingInProgress,
} from 'owa-favorites';
import getSelectedTableView from 'owa-mail-list-store/lib/utils/getSelectedTableView';
import { datapoints as messageDatapoints, lazyNewMessage } from 'owa-mail-message-actions';
import { ActionSource } from 'owa-mail-store';
import * as React from 'react';
import { loadSubjectPickerModule } from 'owa-mail-subject-picker';
import HeaderActions from './../HeaderActions/HeaderActions';
import MultiPersonaControl from '../MultiPersonaControl/MultiPersonaControl';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import { ControlIcons } from 'owa-control-icons';
import strings from 'owa-strings/owa-people-strings';
import { HeaderButtonData } from 'owa-people-persona-header-view';

const styles = require('./PersonaHeader.scss');

export interface MultiPersonaHeaderProps {
    displayName: string;
    personas: EmailAddressWrapper[];
    pdlId?: string;

    favoriteActionSource: ActionSource;

    hideFilter?: boolean;
    hideCheckAll?: boolean;
}

@logMountingPerformance('MultiPersonaHeader')
@observer
export default class MultiPersonaHeader extends React.Component<MultiPersonaHeaderProps, {}> {
    render() {
        const membersCount = this.props.personas.length;

        // TODO-STRINGS: https://msfast.visualstudio.com/FAST/_workitems/edit/251419
        const subtitle = this.props.pdlId
            ? `Contact list • ${membersCount} ${membersCount === 1 ? 'contact' : 'contacts'}`
            : `${membersCount} people`;

        const tableView = getSelectedTableView();

        return (
            <div className={styles.headerContainer}>
                <div className={styles.imageAndInfoContainer}>
                    <MultiPersonaControl personas={this.props.personas} />
                    <div className={styles.personInfoContainer}>
                        <div className={styles.firstLine}>
                            <div className={styles.personName}>{this.props.displayName}</div>
                            <div className={styles.favoriteContainer}>
                                <ToggleFavoriteButton
                                    isInFavorites={this.isFavorite()}
                                    buttonStyles={styles.favoriteButton}
                                    iconStyles={styles.favoriteIcon}
                                    onClick={this.toggleFavoriteState}
                                    animate={true}
                                />
                            </div>
                        </div>
                        <div className={styles.personEmail}>{subtitle}</div>
                    </div>
                </div>
                <HeaderActions
                    tableView={tableView}
                    hideCheckAll={this.props.hideCheckAll}
                    hideFilter={this.props.hideFilter}
                    actionButtonData={this.getActionButtons()}
                />
            </div>
        );
    }

    private getActionButtons(): HeaderButtonData[] {
        return [
            {
                icon: ControlIcons.Mail,
                text: strings.emailButtonLabel,
                clickCallback: this.handleEmailClick,
            },
        ];
    }

    private handleEmailClick = async () => {
        logUsage(messageDatapoints.MailComposeFromHeader.datapoint.name);

        // We need to load module to initialize all the orchestrators and mutators
        await loadSubjectPickerModule();

        if (this.props.pdlId) {
            const recipient: EmailAddressWrapper = {
                ItemId: {
                    Id: this.props.pdlId,
                },
                Name: this.props.displayName,
                MailboxType: 'PrivateDL',
            };
            lazyNewMessage.importAndExecute('MultiPersonaHeader', null /* groupId */, [recipient]);
        } else {
            // From search
            lazyNewMessage.importAndExecute(
                'MultiPersonaHeader',
                null /* groupId */,
                this.props.personas
            );
        }
    };

    private toggleFavoriteState = async (evt: React.MouseEvent<EventTarget>) => {
        evt.stopPropagation();
        const newPdlMembers = this.props.personas.map(persona => ({
            displayName: persona.Name,
            emailAddress: persona.EmailAddress,
        }));

        if (isOutlookFavoritingInProgress('FavoritePdl')) {
            // Ignore add/remove of any favorite if another PDL favoriting is in progress.
            return;
        }

        if (!this.isFavorite()) {
            const addFavoritePrivateDistributionList = await lazyAddFavoritePrivateDistributionList.import();
            addFavoritePrivateDistributionList(
                this.props.displayName,
                newPdlMembers,
                this.props.pdlId,
                'MultiPersonaHeader'
            );
        } else {
            const removeFavoritePrivateDistributionList = await lazyRemoveFavoritePrivateDistributionList.import();

            const favoriteId = getFavoriteIdForPDL(this.props.pdlId, this.props.personas);

            removeFavoritePrivateDistributionList(favoriteId, 'MultiPersonaHeader');
        }
    };

    private isFavorite(): boolean {
        return isPrivateDLInFavorites(this.props.pdlId, this.props.personas);
    }
}
